import { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Schema } from "effect";
import { supportTicketQueryCollectionOptions } from "./support-ticket-query-collection-options";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const USERS_QUERY_RETRY_COUNT = 3;

function usersQueryRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}

/**
 * Backend adapter for the support-v2 users collection. Returns the union
 * of users across every connected team the caller has access to — the
 * frontend slices the result per-ticket via `where(eq(user.accountId, X))`
 * on the TanStack live query.
 */
export interface SupportTicketUsersApi {
  getAccessibleSupportUsers(): Promise<{ users: readonly AssignableUser[] }>;
}

const supportTicketUsersQueryKey = ["supportTicketUsers"] as const;

export function createSupportTicketUsersCollection({
  queryClient,
  api,
}: {
  queryClient: QueryClient;
  api: SupportTicketUsersApi;
}) {
  return createCollection(
    supportTicketQueryCollectionOptions({
      queryKey: supportTicketUsersQueryKey,
      queryFn: async () => api.getAccessibleSupportUsers(),
      select: (data) => [...data.users],
      getKey: (user) => user._id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      retry: USERS_QUERY_RETRY_COUNT,
      retryDelay: usersQueryRetryDelay,
      schema: Schema.standardSchemaV1(AssignableUser),
      meta: { showErrorToast: true },
    }),
  );
}

export type SupportTicketUsersCollection = ReturnType<
  typeof createSupportTicketUsersCollection
>;
